import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { vehicleMakes, getModelsForMake } from "../vehicleData";
import SEO from "../components/SEO";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Upload,
  X,
  CheckCircle,
  Star,
  ArrowRight,
  Loader2,
} from "lucide-react";

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMake, setSelectedMake] = useState("");
  const [availableModels, setAvailableModels] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicleMake: "",
    vehicleModel: "",
    vin: "",
    damageDescription: "",
    images: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVinChange = (e) => {
    // VINs are uppercase and never contain the letters I, O or Q
    const vin = e.target.value
      .toUpperCase()
      .replace(/[^A-HJ-NPR-Z0-9]/g, "")
      .slice(0, 17);
    setFormData((prev) => ({
      ...prev,
      vin,
    }));
  };

  const handleMakeChange = (value) => {
    setSelectedMake(value);
    const models = getModelsForMake(value);
    setAvailableModels(models);
    setFormData((prev) => ({
      ...prev,
      vehicleMake: value,
      vehicleModel: "", // Reset model when make changes
    }));
  };

  const handleModelChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      vehicleModel: value,
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = files.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
      }));

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...newImages].slice(0, 5), // Limit to 5 images
      }));
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => {
      // Clean up preview URL to prevent memory leaks
      if (prev.images[index]?.preview) {
        URL.revokeObjectURL(prev.images[index].preview);
      }

      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== index),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Convert images to base64
      const imagePromises = formData.images.map(async (img) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              name: img.name,
              data: reader.result,
            });
          };
          reader.readAsDataURL(img.file);
        });
      });

      const imagesBase64 = await Promise.all(imagePromises);

      // Send form data to PHP backend
      const response = await fetch("/contact-form.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          vehicleMake: formData.vehicleMake,
          vehicleModel: formData.vehicleModel,
          vin: formData.vin,
          damageDescription: formData.damageDescription,
          images: imagesBase64,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Quote Request Received! 🏁",
          description: result.message,
        });
      } else {
        throw new Error(result.message);
      }

      // Clean up preview URLs
      formData.images.forEach((imageObj) => {
        if (imageObj.preview) {
          URL.revokeObjectURL(imageObj.preview);
        }
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        vehicleMake: "",
        vehicleModel: "",
        vin: "",
        damageDescription: "",
        images: [],
      });
      setSelectedMake("");
      setAvailableModels([]);

      // Reset file input
      const fileInput = document.getElementById("image-upload");
      if (fileInput) fileInput.value = "";
    } catch (error) {
      toast({
        title: "Submission Error",
        description:
          error.message || "Please call (555) 123-4567 for immediate service.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Us - WOW AUTOBODY | Free Auto Body Repair Estimates"
        description="Get a free quote for your auto body repair needs. Upload photos of vehicle damage and receive a detailed estimate within 24 hours. Contact WOW AUTOBODY today."
        keywords="free auto body estimate, collision repair quote, car damage assessment, auto body contact, vehicle repair estimate"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-red-600 text-white mb-6 px-4 py-2">
            Free Estimates Available
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Get Your Free Quote</h1>
          <p className="text-xl leading-relaxed">
            Upload photos of your vehicle damage and get a detailed estimate
            within 24 hours. No obligation, completely free.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="p-8 border-2 hover:border-blue-200 transition-all">
                <CardContent className="p-0">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Request Your Free Quote
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label
                          htmlFor="name"
                          className="text-gray-700 font-semibold"
                        >
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="email"
                          className="text-gray-700 font-semibold"
                        >
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your.email@example.com"
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label
                          htmlFor="phone"
                          className="text-gray-700 font-semibold"
                        >
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          required
                          className="mt-2"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-700 font-semibold">
                          Vehicle Make *
                        </Label>
                        <Select
                          onValueChange={handleMakeChange}
                          value={selectedMake}
                        >
                          <SelectTrigger
                            data-testid="contact-vehicle-make-select"
                            className="mt-2"
                          >
                            <SelectValue placeholder="Select vehicle make" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {vehicleMakes.map((make) => (
                              <SelectItem key={make.value} value={make.value}>
                                {make.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-gray-700 font-semibold">
                        Vehicle Model *
                      </Label>
                      <Select
                        onValueChange={handleModelChange}
                        value={formData.vehicleModel}
                        disabled={!selectedMake}
                      >
                        <SelectTrigger
                          data-testid="contact-vehicle-model-select"
                          className="mt-2 disabled:opacity-50"
                        >
                          <SelectValue
                            placeholder={
                              selectedMake
                                ? "Select vehicle model"
                                : "Select make first"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {availableModels.map((model, index) => (
                            <SelectItem key={index} value={model}>
                              {model}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="vin" className="text-gray-700 font-semibold">
                        VIN (Optional)
                      </Label>
                      <p className="text-sm text-gray-600 mt-1">
                        Your 17-character Vehicle Identification Number helps us
                        quote the exact parts for your vehicle. Found on your
                        dashboard, driver's door jamb, or insurance card.
                      </p>
                      <Input
                        id="vin"
                        name="vin"
                        value={formData.vin}
                        onChange={handleVinChange}
                        placeholder="e.g. 1HGCM82633A004352"
                        maxLength={17}
                        minLength={17}
                        pattern="[A-HJ-NPR-Z0-9]{17}"
                        title="A VIN is 17 characters and does not contain the letters I, O or Q"
                        autoComplete="off"
                        spellCheck="false"
                        className="mt-2 font-mono uppercase tracking-wider"
                        data-testid="contact-vin-input"
                      />
                      {formData.vin.length > 0 && formData.vin.length < 17 && (
                        <p className="text-sm text-amber-600 mt-1">
                          {17 - formData.vin.length} character(s) remaining
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="damageDescription"
                        className="text-gray-700 font-semibold"
                      >
                        Damage Description *
                      </Label>
                      <Textarea
                        id="damageDescription"
                        name="damageDescription"
                        value={formData.damageDescription}
                        onChange={handleInputChange}
                        placeholder="Please describe the damage to your vehicle in detail..."
                        required
                        className="mt-2 h-32"
                      />
                    </div>

                    {/* Image Upload */}
                    <div>
                      <Label className="text-gray-700 font-semibold">
                        Upload Damage Photos
                      </Label>
                      <p className="text-sm text-gray-600 mb-3">
                        Upload up to 5 photos of the damage (JPG, PNG, max 10MB
                        each)
                      </p>

                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="cursor-pointer flex flex-col items-center"
                        >
                          <Upload className="h-12 w-12 text-gray-400 mb-4" />
                          <p className="text-lg font-semibold text-gray-700 mb-2">
                            Click to upload photos
                          </p>
                          <p className="text-sm text-gray-500">
                            or drag and drop images here
                          </p>
                        </label>
                      </div>

                      {/* Image Preview */}
                      {formData.images.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-700 mb-3">
                            {formData.images.length} image(s) selected:
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {formData.images.map((image, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={image.preview}
                                  alt={`Upload ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border"
                                />
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="destructive"
                                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                                  onClick={() => removeImage(index)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                                <p className="text-xs text-gray-600 mt-1 truncate">
                                  {image.name}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-4 text-lg font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Submitting Quote Request...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-5 w-5" />
                          Get My Free Quote
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-gray-600 text-center">
                      By submitting this form, you agree to be contacted by WOW
                      AUTOBODY regarding your quote request.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              {/* Contact Details */}
              <Card className="p-6 border-2 hover:border-blue-200 hover:shadow-lg transition-all">
                <CardContent className="p-0">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Contact Information
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Phone className="h-6 w-6 text-blue-900" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Phone</h4>
                        <p className="text-gray-600">
                          <a href="call:4102925300">
                            (410) 292-5300 (English/Spanish)
                          </a>
                        </p>
                        <p className="text-gray-600">
                          <a href="call:4109804149">(410) 980-4149 (Korean)</a>
                        </p>
                        <p className="text-sm text-gray-500">
                          Call for immediate assistance
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-red-100 p-3 rounded-lg">
                        <Mail className="h-6 w-6 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Email</h4>
                        <p className="text-gray-600">
                          <a href="mailto:info@wowautobody.com">
                            info@wowautobody.com
                          </a>
                        </p>
                        <p className="text-sm text-gray-500">
                          24-hour response guaranteed
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <MapPin className="h-6 w-6 text-blue-900" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Address</h4>
                        <p className="text-gray-600">
                          <a
                            href="https://www.google.com/maps/place/WOW+AUTO+BODY+Collision+Center./@39.2835455,-76.7622896,17z/data=!3m1!4b1!4m6!3m5!1s0x89c81ebe3fce9b2b:0xd63cbcdc9b235843!8m2!3d39.2835414!4d-76.7597147!16s%2Fg%2F11ggs51gc0?entry=ttu&g_ep=EgoyMDI2MDEyOC4wIKXMDSoASAFQAw%3D%3D"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            6427 Balitmore National Pike
                            <br />
                            Cantonsville, MD 21228
                          </a>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-red-100 p-3 rounded-lg">
                        <Clock className="h-6 w-6 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Hours</h4>
                        <div className="text-gray-600 text-sm">
                          <p>Monday - Friday: 9:00 AM - 5:00 PM</p>
                          <p>Saturday: closed</p>
                          <p>Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Why Choose Us */}
              <Card className="p-6 bg-blue-900 text-white">
                <CardContent className="p-0">
                  <h3 className="text-xl font-bold mb-4">
                    Why Choose WOW AUTOBODY?
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Free detailed estimates",
                      "Lifetime warranty on all work",
                      "Insurance claim assistance",
                      "State-of-the-art equipment",
                      "ASE certified technicians",
                    ].map((benefit, index) => (
                      <li key={index} className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 mr-3" />
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white">
                    Call Now for Immediate Service
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-16 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold mb-4">Need Emergency Repairs?</h2>
          <p className="text-lg mb-6 opacity-90">
            We understand accidents happen at any time. Call us for emergency
            collision repairs.
          </p>
          <Button
            size="lg"
            className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
          >
            <Phone className="mr-2 h-5 w-5" />
            Emergency Line: (410) 292-5300
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Contact;
